import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Play, Square, Target, Check } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useElectionDay } from "@/context/ElectionDayContext";

export default function RehearsalPanel() {
  const { ed, refreshEd } = useElectionDay();
  const [committees, setCommittees] = useState([]);
  const [mode, setMode] = useState("all"); // all | pick
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  const active = !!ed?.rehearsal_active;

  useEffect(() => {
    api.get("/committees").then(({ data }) => setCommittees(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (ed?.rehearsal_committees) { setMode("pick"); setPicked(ed.rehearsal_committees); }
  }, [ed?.rehearsal_committees]);

  const run = async (on) => {
    if (on && mode === "pick" && picked.length === 0) {
      toast.error("En az bir komite seçin");
      return;
    }
    setBusy(true);
    try {
      await api.post("/election-day/rehearsal", {
        active: on,
        committees: on && mode === "pick" ? picked : null,
      });
      await refreshEd();
      toast.success(on ? "Prova başlatıldı (sessiz — kimseye bildirim gitmez)" : "Prova bitirildi");
      setOpen(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    } finally {
      setBusy(false);
    }
  };

  const toggleCom = (no) =>
    setPicked((s) => (s.includes(no) ? s.filter((x) => x !== no) : [...s, no]));

  return (
    <Card className={`p-4 border ${active ? "border-emerald-500 bg-emerald-50/60 dark:bg-emerald-950/20" : "border-border"}`}
      data-testid="rehearsal-panel">
      <div className="flex flex-wrap items-start gap-3">
        <span className={`grid h-10 w-10 shrink-0 place-items-center rounded-xl ${
          active ? "bg-emerald-600 text-white" : "bg-muted text-muted-foreground"}`}>
          <Target className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="font-display font-semibold text-base md:text-lg">
            Alıştırma Provası {active && <Badge className="bg-emerald-600 text-white ml-1 align-middle">AKTİF</Badge>}
          </h3>
          <p className="text-sm text-muted-foreground mt-0.5">
            {active
              ? <>Prova açık — {ed.rehearsal_committees
                  ? `yalnız ${ed.rehearsal_committees.length} komite (K${ed.rehearsal_committees.join(", K")})`
                  : "tüm komiteler"}. Tüm kullanıcılar çeteleyi deneyebilir.
                  {ed.rehearsal_started_by ? ` Başlatan: ${ed.rehearsal_started_by}.` : ""}</>
              : "Provayı başlatınca kullanıcılar çeteleyi deneyebilir. Seçim gününe kadar çetele üzerinden yapılan hiçbir işlem için kimseye bildirim gitmez. Prova işaretlemeleri 10 Ekim 22:00'de otomatik sıfırlanır."}
          </p>
        </div>
        {active ? (
          <Button variant="destructive" onClick={() => run(false)} disabled={busy} data-testid="rehearsal-stop">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Square className="h-4 w-4 mr-2" />}
            Provayı Bitir
          </Button>
        ) : (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setOpen((v) => !v)} data-testid="rehearsal-scope-toggle">
              Kapsam: {mode === "all" ? "Tüm komiteler" : `${picked.length} komite`}
            </Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => run(true)}
              disabled={busy} data-testid="rehearsal-start">
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Play className="h-4 w-4 mr-2" />}
              Provayı Başlat
            </Button>
          </div>
        )}
      </div>

      {open && !active && (
        <div className="mt-4 rounded-lg border border-border p-3" data-testid="rehearsal-scope-panel">
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant={mode === "all" ? "default" : "outline"} onClick={() => setMode("all")}
              data-testid="rehearsal-mode-all">Tüm komiteler</Button>
            <Button size="sm" variant={mode === "pick" ? "default" : "outline"} onClick={() => setMode("pick")}
              data-testid="rehearsal-mode-pick">Komite seç</Button>
            {mode === "pick" && (
              <>
                <Button size="sm" variant="ghost" onClick={() => setPicked(committees.map((c) => c.committee_no))}
                  data-testid="rehearsal-pick-all">Hepsini işaretle</Button>
                <Button size="sm" variant="ghost" onClick={() => setPicked([])}
                  data-testid="rehearsal-pick-none">Temizle</Button>
              </>
            )}
          </div>
          {mode === "pick" && (
            <ScrollArea className="h-56 mt-3">
              <div className="grid gap-1.5 sm:grid-cols-2 lg:grid-cols-3 pr-2">
                {committees.map((c) => {
                  const on = picked.includes(c.committee_no);
                  return (
                    <button key={c.committee_no} type="button" onClick={() => toggleCom(c.committee_no)}
                      data-testid={`rehearsal-com-${c.committee_no}`}
                      className={`flex items-center justify-between gap-2 rounded-lg border px-2.5 py-2 text-left text-sm ${
                        on ? "border-emerald-500 bg-emerald-50 dark:bg-emerald-950/30" : "border-border hover:bg-muted/50"}`}>
                      <span className="truncate">{c.committee_no}. {c.name}</span>
                      {on && <Check className="h-4 w-4 shrink-0 text-emerald-600" />}
                    </button>
                  );
                })}
              </div>
            </ScrollArea>
          )}
        </div>
      )}
    </Card>
  );
}
